import { useCallback, useMemo, useState } from 'react';
import { anchorRef, bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import { FilterButton, isFiltersSelected, Popover } from '@occc/ui-design-system';
import { CompletionRange, TransactionFilter, TransactionFragment } from '@occc/graphql';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useGetTransactions } from '@occc-app/dataAccessLayer/transaction/queries';
import { normalizePhoneNumber } from '@occc/utils-phone-number';

import { useConfig } from '@occc/config';
import { TransactionsListPage } from './TransactionsListPage';
import {
  TransactionsFilterForm,
  TransactionsFilterFormProps,
} from './TransactionsFilterForm/transactions-filter-form.component';
import { TransactionsFilterFormValues } from './TransactionsFilterForm/transactions-filter-form.types';
import {
  FRAUD_CHECK_STATUS_ALL,
  TRANSACTION_STATUS_ALL,
} from './TransactionsFilterForm/transactions-filter-form.constants';
import { useTransactionsFilter } from './TransactionsFilterForm/hooks/use-transactions-filter.hook';
import { TransactionFraudulentDetailsModal } from '../components/TransactionFraudulentDetailsModal';

export const filterButtonTestId = 'filter-button';

const TransactionsListPageContainer = () => {
  const popupState = usePopupState({ variant: 'popover', popupId: 'filters' });
  const popoverProps = useMemo(
    () => ({
      ...bindPopover({
        ...popupState,
        isOpen: popupState.isOpen,
      }),
    }),
    [popupState],
  );

  const isFraudCheckEnabled = useConfig('mobileTransactionFraudCheck.enabled');
  const [fraudDetailsTransaction, setFraudDetailsTransaction] =
    useState<TransactionFragment | null>(null);

  const openFraudTransactionModal = useCallback((transaction: TransactionFragment) => {
    setFraudDetailsTransaction(transaction);
  }, []);

  const onFraudTransactionModalClose = useCallback(() => {
    setFraudDetailsTransaction(null);
  }, []);

  const [paginationInfo, onPageChange] = usePaginationParams();

  const {
    locationsFilter,
    transactionCompletedAtFilter,
    startDateFilter,
    endDateFilter,
    transactionStatusFilter,
    fraudCheckStatusFilter,
    amountMinFilter,
    amountMaxFilter,
    phoneNumberFilter,
    onSubmit,
    onReset,
  } = useTransactionsFilter();

  const filter: TransactionFilter = useMemo(
    () => ({
      startedAtMin:
        transactionCompletedAtFilter === CompletionRange.CustomRange && startDateFilter
          ? startDateFilter.toISOString()
          : undefined,
      startedAtMax:
        transactionCompletedAtFilter === CompletionRange.CustomRange && endDateFilter
          ? endDateFilter.toISOString()
          : undefined,
      amountMin: amountMinFilter,
      amountMax: amountMaxFilter,
      locationUuids: locationsFilter?.map((location) => location.uuid),
      fraudCheckStatus: fraudCheckStatusFilter === 'ALL' ? undefined : fraudCheckStatusFilter,
      primaryStatusEquals: transactionStatusFilter === 'ALL' ? undefined : transactionStatusFilter,
      phoneNumber: phoneNumberFilter ? normalizePhoneNumber(phoneNumberFilter) : undefined,
      completionRange: transactionCompletedAtFilter,
    }),
    [
      transactionCompletedAtFilter,
      startDateFilter,
      endDateFilter,
      amountMinFilter,
      amountMaxFilter,
      locationsFilter,
      fraudCheckStatusFilter,
      transactionStatusFilter,
      phoneNumberFilter,
    ],
  );

  const handleFilterSubmit: TransactionsFilterFormProps['onSubmit'] = (...args) => {
    onSubmit(...args);
    popoverProps.onClose();
  };

  const handleFilterReset = () => {
    onReset();
    popoverProps.onClose();
  };

  const { loading, error, data, paginationProps } = useGetTransactions({
    filter,
    paginationInfo,
    onPageChange,
  });

  const filterFormInitialValues: Partial<TransactionsFilterFormValues> = useMemo(() => {
    return {
      locations: locationsFilter,
      transactionCompletedAt: transactionCompletedAtFilter,
      startDate: startDateFilter,
      endDate: endDateFilter,
      phoneNumber: phoneNumberFilter,

      amountMin: amountMinFilter,
      amountMax: amountMaxFilter,

      transactionStatus: transactionStatusFilter,
      fraudCheckStatus: fraudCheckStatusFilter,
    };
  }, [
    locationsFilter,
    transactionCompletedAtFilter,
    startDateFilter,
    endDateFilter,
    phoneNumberFilter,
    amountMinFilter,
    amountMaxFilter,
    transactionStatusFilter,
    fraudCheckStatusFilter,
  ]);

  return (
    <>
      <TransactionsListPage
        loading={loading}
        error={error}
        data={data}
        paginationProps={paginationProps}
        shouldShowFraudStatus={isFraudCheckEnabled ?? false}
        onFraudDetailsClick={openFraudTransactionModal}
        tableHeaderActions={
          <FilterButton
            {...bindTrigger(popupState)}
            ref={anchorRef(popupState)}
            data-testid={filterButtonTestId}
            showBadge={isFiltersSelected(
              [
                locationsFilter && locationsFilter.length > 0 ? locationsFilter : undefined,
                transactionCompletedAtFilter,
                startDateFilter,
                endDateFilter,
                transactionStatusFilter,
                fraudCheckStatusFilter,
                amountMinFilter,
                amountMaxFilter,
                phoneNumberFilter,
              ],
              [TRANSACTION_STATUS_ALL, FRAUD_CHECK_STATUS_ALL],
            )}
          />
        }
      />
      <Popover
        {...popoverProps}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        keepMounted
        realignWhenContentChanged
        PaperProps={{
          sx: {
            maxWidth: 600,
          },
        }}
      >
        <TransactionsFilterForm
          onSubmit={handleFilterSubmit}
          onReset={handleFilterReset}
          initialValues={filterFormInitialValues}
          shouldShowFraudCheckStatusFilter={isFraudCheckEnabled ?? false}
        />
      </Popover>

      {fraudDetailsTransaction && (
        <TransactionFraudulentDetailsModal
          transaction={fraudDetailsTransaction}
          onClose={onFraudTransactionModalClose}
        />
      )}
    </>
  );
};

export default TransactionsListPageContainer;
